import { Badge, Body1, Caption1, Spinner, tokens } from '@fluentui/react-components';
import { DocumentRegular, HistoryRegular, PeopleRegular, PersonRegular } from '@fluentui/react-icons';
import { useApi } from '../../api/useApi';
import { useAsync } from '../../api/useAsync';
import { MemberRow, MemberTypeBar } from '../groups/GroupMembers';
import type { GroupMemberDto, TeamSummaryDto } from '../../api/types';
import '../groups/groups.css';
import './teams.css';

function SubList({ title, people }: { title: string; people: GroupMemberDto[] }) {
  return (
    <div>
      <div className="grp-sublist-title">
        {title} ({people.length})
      </div>
      {people.length === 0 ? (
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>—</Caption1>
      ) : (
        <div className="grp-sublist">
          {people.map((m) => (
            <MemberRow key={m.id} member={m} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Read-only-Detail eines Teams: Impact-Kennzahlen, Beschreibung, Owner und Mitglieder. */
export function TeamDetailView({ team }: { team: TeamSummaryDto }) {
  const api = useApi();
  const detail = useAsync(() => api.getGroup(team.id), [api, team.id]);
  const d = detail.data;

  return (
    <div className="grp-panel">
      <div className="grp-panel-head">
        <span className="grp-panel-title">{team.displayName}</span>
        <Badge appearance="tint" color="brand">
          Team
        </Badge>
      </div>

      <span className="tm-impact-row">
        <span className="tm-impact" title="Mitglieder">
          <PeopleRegular />
          {team.memberCount} Mitglieder
        </span>
        <span className="tm-impact" title="Gäste">
          <PersonRegular />
          {team.guestCount} Gäste
        </span>
        <span className="tm-impact" title="Dateien">
          <DocumentRegular />
          {team.fileCount ?? '—'} Dateien
        </span>
        <span className="tm-impact" title="Letzte Aktivität">
          <HistoryRegular />
          {team.lastActivity ?? '—'}
        </span>
      </span>

      {d?.description ? <Body1 className="grp-desc">{d.description}</Body1> : null}
      {detail.loading ? <Spinner size="tiny" label="Lade Details…" /> : null}
      {detail.error ? (
        <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{detail.error.message}</Caption1>
      ) : null}
      {d ? (
        <>
          <MemberTypeBar members={d.members} transitiveCount={d.transitiveMemberCount} />
          <SubList title="Owner" people={d.owners} />
          <SubList title="Mitglieder" people={d.members.filter((m) => !m.isGuest)} />
          <SubList title="Gäste" people={d.members.filter((m) => m.isGuest)} />
        </>
      ) : null}
    </div>
  );
}
